import { DashboardApiTypes } from 'api'
import { TOTAL, DIAGRAMS } from 'texts/dashboard'
import { ApexOptions } from 'apexcharts'
import { DS_COLORS } from '@frontend/design_system/src/constants/designSystemColors'
import {
  GroupItem,
  DiagramOptions,
  GroupPerDayItem,
  TotalRevenue,
  GroupCarRentalItem,
  DiagramSettings,
} from './types'
import {
  COMPLETED_TYPE,
  SCHEDULED_TYPE,
  NOT_AVAILABLE,
} from './components/CarRentalDays/constants'
import {
  MONTHS,
  ONE_GROUP,
  AFTER_DOT,
  MONTH_LENGTH,
  TWO_GROUP,
  diagramVariant,
  PERIOD,
} from './constants'
import { formatNumberWithCommas } from 'utils/formatters'
import { add, format } from 'date-fns'
import { DASHBOARD_DIAGRAM_FORMAT } from 'constants/date'

const DONUT_COLORS = [
  DS_COLORS.blue900Master,
  DS_COLORS.blue700,
  DS_COLORS.blue500,
  DS_COLORS.blue300,
  DS_COLORS.blue200,
  DS_COLORS.blue100,
  DS_COLORS.green900,
  DS_COLORS.green700Master,
  DS_COLORS.green500,
  DS_COLORS.green300,
  DS_COLORS.green200,
  DS_COLORS.green100,
  DS_COLORS.yellow900,
  DS_COLORS.yellow700,
  DS_COLORS.yellow500,
  DS_COLORS.yellow300,
  DS_COLORS.yellow200,
  DS_COLORS.yellow100,
  DS_COLORS.red900,
  DS_COLORS.red700,
  DS_COLORS.red500,
  DS_COLORS.red300,
  DS_COLORS.red200,
  DS_COLORS.red100,
  DS_COLORS.grey900,
  DS_COLORS.grey500,
]

export const CAR_GROUP_COLORS = {
  COMPACT: '#6780E6',
  CONVERTIBLE: '#FFE3B0',
  ECONOMY: '#8FCDFF',
  ELECTRIC: '#C0EFC5',
  ESTATE: '#FFBFA0',
  FULLSIZE: '#CFB9F1',
  LUXURY: '#C4C4C4',
  MIDSIZE: '#F7C2C2',
  OTHER: '#AFE9DD',
  SMALL: '#F2C2EB',
  SPECIAL: '#EAD5BA',
  SUV: '#DDEBB9',
  VAN: '#FCEF9E',
}

const ONE_GROUP_COLORS = ['#3756DD', '#B0BDF2'] // will be added after DS update
const TWO_GROUP_COLORS = ['#3756DD', '#B0BDF2']

export const groupedCarRentalItems = (
  items: DashboardApiTypes.CarRentalDaysItem[]
) => {
  const years: string[] = []
  const groupItems = items.reduce<Record<string, GroupCarRentalItem>>(
    (acc, item) => {
      const completed =
        item.breakdownType === COMPLETED_TYPE ? item.rentalDays : NOT_AVAILABLE
      const scheduled =
        item.breakdownType === SCHEDULED_TYPE ? item.rentalDays : NOT_AVAILABLE
      const year = item.month.split('-')[0]
      if (!years.includes(year)) {
        years.push(year)
      }

      if (acc[item.month]) {
        if (completed !== NOT_AVAILABLE) {
          acc[item.month].completed = completed
        }
        if (scheduled !== NOT_AVAILABLE) {
          acc[item.month].scheduled = scheduled
        }
      } else {
        acc[item.month] = {
          date: item.month,
          scheduled,
          completed,
        }
      }

      return acc
    },
    {}
  )

  return { yearsCount: years.length, groupItems }
}

const formatApexSeries = (
  data: (GroupItem | GroupPerDayItem)[]
): ApexOptions['series'] =>
  data.map(({ name, group, data }) => ({
    name,
    group,
    data: data.map((itemData) => {
      if (!itemData) return null

      if (typeof itemData === 'object' && 'value' in itemData) {
        return itemData.value
      }

      return typeof itemData === 'string' ? parseFloat(itemData) : itemData
    }),
  }))

export const formatCarRentalData = (
  data: DashboardApiTypes.CarRentalDaysResponse | null
) => {
  let result: GroupItem[] = []
  let groupCount = 0

  data?.items.forEach((item) => {
    const [year, month] = item.month.split('-')
    const monthIndex = parseInt(month, 10) - 1

    let group = result.find(
      (itemRes) => itemRes.name === item.breakdownType && itemRes.group === year
    )

    if (!group) {
      group = {
        name: item.breakdownType,
        group: year,
        data: Array(MONTHS.length).fill(null),
      }
      result.push(group)
      groupCount++
    }

    group.data[monthIndex] = item.rentalDays
  })

  const uniqueGroups = new Set(result.map((item) => item.group))
  groupCount = uniqueGroups.size

  if (groupCount > ONE_GROUP) {
    result = result.map((item) => ({
      ...item,
      name: `${item.name} ${item.group}`,
    }))
  }

  return {
    formatData: formatApexSeries(result),
    groupCount,
  }
}

export const formatPerDayData = (
  data: DashboardApiTypes.RevenuePerDayResponse | null
) => {
  {
    let revenuePerDayResult: GroupPerDayItem[] = []
    let revenueRentalDaysResult: GroupPerDayItem[] = []
    let revenueResult: GroupPerDayItem[] = []
    let categories: string[] = []
    let groupCount = 0

    if (data?.items && data.items.length > MONTH_LENGTH) {
      categories = MONTHS
      data.items.forEach((item) => {
        const [year, month] = item.date.split('-')
        const monthIndex = parseInt(month, 10) - 1

        const group: GroupPerDayItem | undefined = revenuePerDayResult.find(
          (itemRes: GroupPerDayItem) => itemRes.group && itemRes.group === year
        )

        if (!group) {
          revenuePerDayResult.push({
            name: DIAGRAMS.REVENUE_PER_DAY,
            group: year,
            data: Array(MONTHS.length).fill(null),
            counts: Array(MONTHS.length).fill(0),
          })
          revenueRentalDaysResult.push({
            name: DIAGRAMS.RENTAL_DAYS,
            group: year,
            data: Array(MONTHS.length).fill(null),
            counts: [],
          })
          revenueResult.push({
            name: DIAGRAMS.REVENUE,
            group: year,
            data: Array(MONTHS.length).fill(null),
            counts: [],
          })
          groupCount++
        }

        const findItem = revenuePerDayResult.findIndex(
          (item) => item.group === year
        )
        if (revenueResult[findItem].data[monthIndex] === null) {
          revenuePerDayResult[findItem].data[monthIndex] = item.revenuePerDay
          revenuePerDayResult[findItem].counts[monthIndex] = 1
          revenueRentalDaysResult[findItem].data[monthIndex] = item.rentalDays
          revenueResult[findItem].data[monthIndex] = item.revenue
        } else {
          revenuePerDayResult[findItem].data[monthIndex] = Number(
            Number(revenuePerDayResult[findItem].data[monthIndex]) +
              item.revenuePerDay
          ).toFixed(AFTER_DOT)
          revenuePerDayResult[findItem].counts[monthIndex] =
            Number(revenuePerDayResult[findItem].counts[monthIndex]) + 1
          revenueRentalDaysResult[findItem].data[monthIndex] = Number(
            Number(revenueRentalDaysResult[findItem].data[monthIndex]) +
              item.rentalDays
          ).toFixed(AFTER_DOT)
          revenueResult[findItem].data[monthIndex] = Number(
            Number(revenueResult[findItem].data[monthIndex]) + item.revenue
          ).toFixed(AFTER_DOT)
        }
      })
      revenuePerDayResult = revenuePerDayResult.map((itemPerDay) => ({
        ...itemPerDay,
        data: itemPerDay.data.map((item, index) => {
          if (item && itemPerDay.counts[index] !== null) {
            const count = itemPerDay.counts[index] || 1
            return Number(Number(item) / count).toFixed(AFTER_DOT)
          }
          return item
        }),
      }))
    } else {
      revenuePerDayResult.push({
        name: DIAGRAMS.REVENUE_PER_DAY,
        data: [],
        counts: [],
      })
      revenueResult.push({
        name: DIAGRAMS.REVENUE,
        data: [],
        counts: [],
      })
      revenueRentalDaysResult.push({
        name: DIAGRAMS.RENTAL_DAYS,
        data: [],
        counts: [],
      })
      data?.items.forEach((item) => {
        const [, month, day] = item.date.split('-')
        categories.push(`${day}.${month}`)
        revenuePerDayResult[0].data.push(item.revenuePerDay || null)
        revenueResult[0].data.push(item.revenue || null)
        revenueRentalDaysResult[0].data.push(item.rentalDays || null)
      })
    }

    if (groupCount > ONE_GROUP) {
      revenuePerDayResult = revenuePerDayResult.map((item) => ({
        ...item,
        name: `${item.name} ${item.group}`,
      }))
      revenueResult = revenueResult.map((item) => ({
        ...item,
        name: `${item.name} ${item.group}`,
      }))
      revenueRentalDaysResult = revenueRentalDaysResult.map((item) => ({
        ...item,
        name: `${item.name} ${item.group}`,
      }))
    }

    return {
      revenuePerDay: formatApexSeries(revenuePerDayResult),
      revenue: formatApexSeries(revenueResult),
      rentalDays: formatApexSeries(revenueRentalDaysResult),
      groupCount,
      categories,
    }
  }
}

export const formatPerUnitData = (
  data: DashboardApiTypes.RevenuePerUnitResponse | null
) => {
  const revenuePerDayResult: (string | number | null)[] = []
  const revenueReservationsResult: (string | number | null)[] = []
  const revenueResult: (string | number | null)[] = []
  const categories: string[] = []

  data?.items.forEach((item) => {
    categories.push(item.carType)
    revenuePerDayResult.push(item.revenuePerReservation || null)
    revenueResult.push(item.revenue || null)
    revenueReservationsResult.push(item.reservations || null)
  })

  return {
    revenuePerDay: formatApexSeries([
      {
        name: DIAGRAMS.REVENUE_PER_INIT,
        data: revenuePerDayResult,
      },
    ]),
    revenue: formatApexSeries([
      {
        name: DIAGRAMS.REVENUE,
        data: revenueResult,
      },
    ]),
    reservations: formatApexSeries([
      {
        name: DIAGRAMS.RESERVATIONS,
        data: revenueReservationsResult,
      },
    ]),
    categories,
  }
}

export const formatRevenueTotalData = (
  data: DashboardApiTypes.RevenuePerUnitResponse | null
): TotalRevenue => {
  const revenueResult: number[] = []
  const categories: string[] = []

  data?.items.forEach((item) => {
    categories.push(item.carType)
    revenueResult.push(Number(item.revenue))
  })

  return {
    revenue: revenueResult,
    categories,
  }
}

const getColors = (groupCount: number) => {
  if (groupCount <= ONE_GROUP) {
    return ONE_GROUP_COLORS
  }
  if (groupCount <= TWO_GROUP) {
    return TWO_GROUP_COLORS
  }
  return Object.values(CAR_GROUP_COLORS)
}

export const getDonutOption = (
  categoryOptions: string[],
  total: string
): ApexOptions => ({
  chart: {
    type: 'donut',
  },
  labels: categoryOptions,
  plotOptions: {
    pie: {
      donut: {
        size: '60%',
        background: 'transparent',
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: '24px',
            fontFamily: 'Inter, Roboto, sans-serif',
            fontWeight: 800,
            color: '#000',
            offsetY: -10,
            formatter: function (val: string) {
              return val
            },
          },
          total: {
            show: true,
            fontSize: '16px',
            fontFamily: 'Inter, Roboto, sans-serif',
            fontWeight: 800,
            color: '#000',
            label: `${TOTAL}:`,
            formatter: () => total || '',
          },
        },
      },
    },
  },
  colors: DONUT_COLORS,
  stroke: {
    show: true,
    width: 5,
    colors: ['#ffffff'],
  },
  dataLabels: {
    enabled: true,
    dropShadow: {
      enabled: false,
    },
  },
  legend: {
    show: true,
    position: 'right',
    horizontalAlign: 'center',
    fontSize: '16px',
    fontFamily: 'Inter, Roboto, sans-serif',
    fontWeight: 500,
    labels: {
      colors: ['#373D3F'],
      useSeriesColors: false,
    },
    markers: {
      size: 10,
      offsetX: -6,
    },
    itemMargin: {
      vertical: 8,
    },
    offsetX: 50,
    offsetY: 0,
  },
  states: {
    active: {
      allowMultipleDataPointsSelection: false,
      filter: {
        type: 'none',
      },
    },
  },
})

const MIN_HEIGHT = 40
const MAX_HEIGHT = 80

const SETTINGS: DiagramSettings = {
  schedule: {
    withDataLabels: true,
    isStacked: true,
    withCustomTooltip: false,
    borderRadius: 4,
    hideZeroBarsWhenGrouped: false,
  },
  perCar: {
    withDataLabels: true,
    isStacked: false,
    withCustomTooltip: false,
    hideZeroBarsWhenGrouped: false,
    borderRadius: 0,
  },
  perCarClass: {
    withDataLabels: false,
    isStacked: true,
    withCustomTooltip: true,
    hideZeroBarsWhenGrouped: true,
    borderRadius: 4,
  },
}

export const getDiagramOptions = ({
  categoryOptions,
  groupCount = 0,
  currency = '',
  variant,
  period,
}: DiagramOptions): ApexOptions => ({
  chart: {
    type: 'bar',
    animations: { enabled: false },
    width: '100%',
    stacked: SETTINGS[variant].isStacked,
    height: 350,
    redrawOnParentResize: false,
    toolbar: {
      show: false,
    },
  },
  plotOptions: {
    bar: {
      horizontal: false,
      borderRadius: SETTINGS[variant].borderRadius,
      columnWidth: '80%',
      borderRadiusApplication: 'around',
      borderRadiusWhenStacked: 'all',
      hideZeroBarsWhenGrouped: SETTINGS[variant].hideZeroBarsWhenGrouped,
      dataLabels: {
        orientation:
          variant === diagramVariant.perCar ? 'vertical' : 'horizontal',
        hideOverflowingLabels: true,
        total: {
          formatter: (val) => (Number(val) === 0 ? '0' : ''),
          enabled: true,
          style: {
            fontSize: '10px',
            fontWeight: 500,
          },
        },
      },
    },
  },
  colors: getColors(groupCount),
  dataLabels: {
    style: {
      fontSize: '10px',
      colors: variant === diagramVariant.perCar ? ['#000'] : ['#fff'],
    },
    enabled: SETTINGS[variant].withDataLabels,
    offsetY: 2,
    formatter: function (val, opts) {
      const MAX_RATIO = 9
      const maxValue = Math.max(
        ...opts.w.config.series.map((obj: { name: string; data: number[] }) =>
          Math.max(...obj.data)
        )
      )
      if (Number(val) !== 0 && Number(val) < maxValue / MAX_RATIO) {
        return ''
      }

      if (
        (variant === diagramVariant.perCar && Number(val) === 0) ||
        Number(val) < maxValue / MAX_RATIO
      ) {
        return ''
      }
      return formatNumberWithCommas(Number(val?.toString().split('.')[0]))
    },
    textAnchor: 'middle',
    dropShadow: {
      enabled: false,
    },
  },
  legend: {
    position: 'bottom',
    horizontalAlign: variant === diagramVariant.schedule ? 'center' : 'left',
    fontSize: '14px',
    show: true,
    floating: false,
    showForSingleSeries: true,
    showForZeroSeries: true,
    height: variant === diagramVariant.schedule ? MIN_HEIGHT : MAX_HEIGHT,
    showForNullSeries: true,
    fontFamily: 'Inter, Roboto, sans-serif',
    fontWeight: 500,
    labels: {
      colors: ['#373D3F'],
      useSeriesColors: false,
    },
    markers: {
      size: 8,
      customHTML: () => `<div style="
          width: 14px; 
          height: 14px; 
          border-radius: 4px; 
          background-color: currentColor;">
        </div>`,
    },
    itemMargin: {
      horizontal: 24,
    },
  },
  stroke: {
    show: true,
    width: 2,
    colors: ['transparent'],
    lineCap: 'round',
  },
  xaxis: {
    type: 'category',
    categories: categoryOptions.map((value) => {
      if (typeof value === 'string') {
        const date = new Date(value)
        const day = date.getDate()
        const month = date.toLocaleString('en-US', { month: 'short' })
        if (period === 'Month') {
          return date.toLocaleString('en-US', { month: 'long' })
        }
        return SETTINGS[variant].isStacked
          ? [day, month]
          : date.toLocaleString('en-US', { month: 'long' })
      }
      return ''
    }),
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: {
        fontSize: '10px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        fontSize: '10px',
      },
    },
  },
  grid: {
    show: true,
    borderColor: '#e0e0e0',
    strokeDashArray: 5,
    yaxis: {
      lines: {
        show: true,
      },
    },
  },
  fill: {
    opacity: 1,
  },
  states: {
    normal: {
      filter: {
        type: 'none',
        value: 0,
      },
    },
    hover: {
      filter: {
        type: 'darken',
        value: 0.65,
      },
    },
    active: {
      allowMultipleDataPointsSelection: false,
      filter: {
        type: 'darken',
        value: 0.35,
      },
    },
  },
  tooltip: SETTINGS[variant].withCustomTooltip
    ? {
        enabled: true,
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex, w }) => {
          const getTooltipItem = (
            color: string,
            name: string,
            value: number
          ) => `
                <div style="
                  font-size: 12px; 
                  height: 24px; 
                  color: #7B7B7B; 
                  width: 100%; 
                  display: flex; 
                  justify-content: space-between;">
                  
                  <div style="display: flex; gap: 4px;">
                    <div style="
                      width: 14px; 
                      height: 14px; 
                      border-radius: 4px; 
                      background-color: ${color};">
                    </div>
                    ${name}
                  </div> 

                  <span style="font-size: 14px; color: black; font-weight: 500;">
                    ${formatNumberWithCommas(value || 0)}
                  </span>
                </div>
              `
          const groupValues = w.config.series.map(
            (serie: { name: string; data: number[] }) =>
              serie.data[dataPointIndex]
          )

          const total = groupValues.reduce(
            (acc: number, value: number) => acc + (value || 0),
            0
          )

          const periodStart: string | string[] =
            w.config.xaxis.categories[dataPointIndex]

          const currentYear = new Date().getFullYear()

          const getDisplayedPeriod = () => {
            if (period === PERIOD.Months || !Array.isArray(periodStart)) {
              return `${periodStart} ${currentYear}`
            }

            const formattedStart = `${periodStart.join(' ')} ${currentYear}`
            return period === PERIOD.Weeks
              ? `${formattedStart} - ${format(
                  add(new Date(formattedStart), { days: 6 }),
                  DASHBOARD_DIAGRAM_FORMAT
                )}`
              : formattedStart
          }

          const tooltipHeader = `
            <div style="
              font-size: 14px; 
              font-weight: bold; 
              padding: 12px; 
              border-bottom: 1px solid #ECECEC; 
              display: flex; 
              justify-content: space-between;
              gap: 16px;
              min-width: 220px;
            ">
              <span style="
                color: #7B7B7B; 
                font-weight: 400; 
                font-size: 12px;
              ">
                ${getDisplayedPeriod()}
              </span>  
              <div>
                ${formatNumberWithCommas(total)} ${currency}
              </div>
            </div>
          `

          let firstColumnContent = ''
          let secondColumnContent = ''
          const FIRST_COLUMN_LAST_INDEX = 7
          const firstColumn = w.config.series.slice(0, FIRST_COLUMN_LAST_INDEX)
          const secondColumn = w.config.series.slice(FIRST_COLUMN_LAST_INDEX)

          firstColumn.forEach(
            (serie: { name: string; data: number[] }, index: number) => {
              const color = w.config.colors[index]
              firstColumnContent += getTooltipItem(
                color,
                serie.name,
                serie.data[dataPointIndex]
              )
            }
          )

          secondColumn.forEach(
            (serie: { name: string; data: number[] }, index: number) => {
              const color = w.config.colors[FIRST_COLUMN_LAST_INDEX + index]

              secondColumnContent += getTooltipItem(
                color,
                serie.name,
                serie.data[dataPointIndex]
              )
            }
          )

          return `
          <div style="
            height: 220px; 
            border-radius: 6px;
          ">
            ${tooltipHeader}   
            <div style="display: flex;">
              <div style="
                margin-top: 4px;
                padding: 4px 16px; 
                width: ${secondColumnContent ? '220px' : '100%'};
              ">
                ${firstColumnContent}
              </div>
              ${
                secondColumnContent
                  ? `<div style="
                border-left: 1px solid #ECECEC; 
                margin-top: 4px;
                margin-bottom: 8px;
                padding: 4px 16px; 
                width: 220px;
              ">
                ${secondColumnContent}
              </div>`
                  : ''
              }
            </div>
          </div>`
        },
      }
    : {
        y: {
          formatter: (val: number) => formatNumberWithCommas(val),
        },
      },
})
